import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "../api";
import { logoUrl } from "../branding";
import { useSession } from "./session";
import type { AppNotification, Role, SearchResults } from "../../shared/types";

const RANK: Record<Role, number> = { volunteer: 1, member: 2, coordinator: 3, leader: 4, owner: 5 };

const NAV: Array<{ label: string; items: Array<[string, string, Role]> }> = [
  { label: "Overview", items: [["/", "Home", "volunteer"], ["/tasks", "Tasks", "volunteer"], ["/messages", "Outbox", "coordinator"]] },
  { label: "Music", items: [["/services", "Services", "volunteer"], ["/songs", "Songs", "volunteer"]] },
  { label: "Community", items: [["/people", "People", "volunteer"], ["/groups", "Groups", "volunteer"], ["/workflows", "Workflows", "volunteer"]] },
  { label: "Operations", items: [["/calendar", "Calendar", "volunteer"], ["/registrations", "Registrations", "coordinator"], ["/check-in", "Check-in", "coordinator"], ["/giving", "Giving", "coordinator"], ["/publishing", "Publishing", "leader"], ["/portal", "Portal", "volunteer"], ["/settings", "Settings", "volunteer"]] },
];

export function Shell() {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const [notes, setNotes] = useState<AppNotification[]>([]);
  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => {
    void api<AppNotification[]>("/api/v1/notifications").then(setNotes).catch(() => setNotes([]));
  }, [user?.id]);

  useEffect(() => {
    if (query.trim().length < 2) return;
    const handle = window.setTimeout(() => {
      void api<SearchResults>(`/api/v1/search?q=${encodeURIComponent(query)}`).then(setResults).catch(() => setResults(null));
    }, 180);
    return () => window.clearTimeout(handle);
  }, [query]);

  const unread = notes.filter((note) => !note.readAt).length;
  const role = user?.role ?? "volunteer";
  const allowed = NAV.map((group) => ({ ...group, items: group.items.filter((item) => RANK[role] >= RANK[item[2]]) })).filter((group) => group.items.length > 0);
  return (
    <div className="app-shell">
      <a className="skip" href="#content">Skip to content</a>
      <aside className={open ? "sidebar open" : "sidebar"}>
        <NavLink to="/" className="brand" onClick={() => setOpen(false)}>
          <img className="brand-logo" src={logoUrl} alt="" />
          <span><strong>MB Live</strong><span>Moksha Base</span></span>
        </NavLink>
        {allowed.map((group) => (
          <div className="nav-group" key={group.label}>
            <p>{group.label}</p>
            {group.items.map(([to, label]) => (
              <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} onClick={() => setOpen(false)}>{label}</NavLink>
            ))}
          </div>
        ))}
        <div className="sidebar-foot">Local workspace · providers off</div>
      </aside>
      <div className="workspace">
        <header className="topbar">
          <button className="icon-btn menu-btn" type="button" aria-label="Open navigation" onClick={() => setOpen((value) => !value)}>☰</button>
          <form className="search" onSubmit={(event) => { event.preventDefault(); navigate(`/search?q=${encodeURIComponent(query)}`); setResults(null); }}>
            <input aria-label="Search songs, people, and plans" placeholder="Search songs, people, plans" value={query} onChange={(event) => setQuery(event.target.value)} />
            {results && query.trim().length > 1 && (
              <div className="suggest">
                {[...results.songs.map((item) => ({ ...item, href: `/songs/${item.id}` })), ...results.people.map((item) => ({ ...item, href: `/people/${item.id}` })), ...results.plans.map((item) => ({ ...item, href: `/services/${item.id}` })), ...results.events.map((item) => ({ ...item, href: `/calendar/${item.id}` }))].slice(0, 8).map((item) => (
                  <a key={item.href} href={item.href} onClick={(event) => { event.preventDefault(); setResults(null); navigate(item.href); }}>{item.label}<small>{item.detail}</small></a>
                ))}
              </div>
            )}
          </form>
          <div className="top-actions">
            {RANK[role] >= RANK.leader && <button className="btn small" type="button" onClick={() => navigate("/services/new")}>New plan</button>}
            <button className="icon-btn notification-btn" type="button" aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"} onClick={() => setShowNotes((value) => !value)}>
              <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>
              {unread > 0 && <span className="notification-count">{unread > 9 ? "9+" : unread}</span>}
            </button>
            <div className="user-chip"><strong>{user?.name}</strong><span>{user?.role}</span></div>
            <button className="btn ghost small" type="button" onClick={() => void signOut()}>Sign out</button>
          </div>
        </header>
        {showNotes && (
          <div className="panel panel-pad" style={{ margin: "0 22px" }}>
            <div className="split"><strong>Notifications</strong><button className="btn small ghost" type="button" onClick={() => void api("/api/v1/notifications/read-all", { method: "POST" }).then(() => setNotes(notes.map((note) => ({ ...note, readAt: new Date().toISOString() }))))}>Mark read</button></div>
            <div className="stack" style={{ marginTop: 10 }}>
              {notes.length === 0 && <p className="muted">Nothing waiting.</p>}
              {notes.map((note) => <a key={note.id} href={note.href ?? "/"}>{note.title}<div className="faint">{note.body}</div></a>)}
            </div>
          </div>
        )}
        <main id="content"><Outlet /></main>
      </div>
    </div>
  );
}
